import { useEffect, useRef, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { Can } from "@core/permissions";
import { useSession } from "@core/session/SessionProvider";
import { ApiError } from "@core/http";
import { CursorTable, type CursorTableColumn } from "@core/table";
import type { BilingualText, GrandPrize } from "@core/types";
import { tokens } from "@core/theme/tokens";
import { ImageWithPlaceholder } from "@core/media";
import {
  Badge,
  EmptyState,
  FieldError,
  FileUploadChip,
  ImpactConfirmDialog,
  InfoNotice,
  inputStyle,
  LanguageSwitch,
  PortalButton,
  PrizesHeader,
  RequiredMark,
  RowIconAction,
  useConfirmUnsavedChanges,
  useToast,
  type ContentLanguage,
} from "@portal/common";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import type { GrandPrizeImpact, GrandPrizeSaveInput } from "./api";
import { useGrandPrizeMutations, useGrandPrizes } from "./grandPrizeQueries";
import { Ring } from "./Ring";
import {
  grandPrizeFormSchema,
  type GrandPrizeFormValues,
} from "./grandPrizeSchemas";

// Ventana propuesta por defecto al crear (SPEC-208 §3:
// `gran_premio.ventana_meses_defecto`, inicial 12).
const DEFAULT_WINDOW_MONTHS = 12;

// Vista previa ilustrativa "Así se ve el avance en la app" (admin.jsx:1508):
// el prototipo fija n=7 sin depender de un miembro real — no hay progreso
// real que mostrar acá porque esta pantalla no representa a ningún miembro
// en particular. Se conserva el mismo valor ilustrativo.
const PREVIEW_PROGRESS = 7;

function text(value: BilingualText | undefined | null, lang: ContentLanguage) {
  if (!value) return "";
  return value[lang] ?? "";
}

type Selection =
  { mode: "existing"; prize: GrandPrize } | { mode: "new"; seed?: GrandPrize };

function defaultsFor(selection: Selection): GrandPrizeFormValues {
  if (selection.mode === "new" && !selection.seed) {
    return {
      nameEs: "",
      nameEn: "",
      descriptionEs: "",
      descriptionEn: "",
      imageUrl: "",
      imageFileId: "",
      goal: 1,
      windowMonths: DEFAULT_WINDOW_MONTHS,
      active: false,
    };
  }
  const source =
    selection.mode === "existing" ? selection.prize : selection.seed!;
  return {
    nameEs: text(source.name, "es"),
    nameEn: text(source.name, "en"),
    descriptionEs: text(source.description, "es"),
    descriptionEn: text(source.description, "en"),
    imageUrl: source.imageUrl ?? "",
    // QA-MTZ-002 ronda 3 (#5): la copia comparte el archivo del vigente. Sin
    // esto guardaba la URL firmada de la lectura, que vence a los 15 minutos.
    imageFileId: source.imageFileId ?? "",
    goal: source.goal,
    windowMonths: source.windowMonths,
    // Copiar el vigente (PG-002) crea un premio nuevo en borrador: nunca
    // arranca activo, para no reemplazar al vigente por accidente al
    // guardar sin que el administrador lo decida explícitamente.
    active: selection.mode === "existing" ? source.active : false,
  };
}

function buildInput(values: GrandPrizeFormValues): GrandPrizeSaveInput {
  const name: BilingualText = {
    es: values.nameEs,
    ...(values.nameEn ? { en: values.nameEn } : {}),
  };
  const hasDescription = values.descriptionEs || values.descriptionEn;
  const description: BilingualText | undefined = hasDescription
    ? {
        es: values.descriptionEs,
        ...(values.descriptionEn ? { en: values.descriptionEn } : {}),
      }
    : undefined;
  return {
    name,
    ...(description ? { description } : {}),
    ...(values.imageUrl ? { imageUrl: values.imageUrl } : {}),
    // PLAN-007 Fase 4d: viaja solo cuando el operador subió un archivo
    // nuevo en esta sesión del formulario; el backend le da prioridad
    // sobre `imageUrl` (GrandPrizeInput.imageFileId).
    ...(values.imageFileId ? { imageFileId: values.imageFileId } : {}),
    goal: values.goal,
    windowMonths: values.windowMonths,
    active: values.active,
  };
}

// Field local a esta pantalla (no el `Field` compartido de
// web/src/portal/common/FormField.tsx): el compartido rotula con
// `font: 600 12px` y sin `hint`, valor reutilizado por ProfileForm/UserForm/
// CatalogEntryForm — cambiarlo movería esas pantallas. La captura canónica
// (admin.jsx:83-90, clase `.label`: 10.5px, letterSpacing 0.18em, uppercase,
// `--text3`) exige esa tipografía puntual solo acá, así que se compone en la
// feature en vez de tocar el primitivo compartido.
function GPField({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label style={{ display: "block", marginBottom: 16 }}>
      <div className="label" style={{ marginBottom: 8 }}>
        {label}
        {required ? <RequiredMark /> : null}
      </div>
      {children}
      {hint ? (
        <div
          style={{
            font: `400 11px/1.45 ${tokens.font.ui}`,
            color: tokens.text.disabled,
            marginTop: 6,
          }}
        >
          {hint}
        </div>
      ) : null}
      {error ? <FieldError message={error} /> : null}
    </label>
  );
}

// Misma tipografía y espaciado que `GPField`, pero SIN el `<label>`: el
// campo "Imagen del premio" contiene un botón ("Reemplazar imagen"), y un
// botón anidado dentro de un `<label>` rompe su nombre accesible (mismo bug
// ya documentado más abajo para "Estado" — verificado igual acá con
// @testing-library/dom: sin este componente, `getByRole("button", { name:
// "Reemplazar imagen" })` no encontraba el botón).
function GPFieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="label" style={{ marginBottom: 8 }}>
        {label}
      </div>
      {children}
      {hint ? (
        <div
          style={{
            font: `400 11px/1.45 ${tokens.font.ui}`,
            color: tokens.text.disabled,
            marginTop: 6,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

interface GrandPrizeFormProps {
  selection: Selection;
  canWrite: boolean;
  submitting: boolean;
  onSubmit: (id: string | undefined, input: GrandPrizeSaveInput) => void;
  onDiscard: () => void;
}

// QA-MTZ-002 ronda 3 (#5): «Copiar el vigente» parecía no hacer nada —
// el formulario quedaba igual (mismos datos, mismo título) y fuera de
// pantalla. El título dice ahora qué se está editando.
function formTitle(selection: Selection): string {
  if (selection.mode === "existing") return "Configuración del gran premio";
  return selection.seed
    ? "Nuevo premio · copia del vigente"
    : "Nuevo gran premio";
}

function GrandPrizeForm({
  selection,
  canWrite,
  submitting,
  onSubmit,
  onDiscard,
}: GrandPrizeFormProps) {
  const [language, setLanguage] = useState<ContentLanguage>("es");
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
    setError,
    reset,
  } = useForm<GrandPrizeFormValues>({ defaultValues: defaultsFor(selection) });
  useConfirmUnsavedChanges(isDirty);
  const goal = watch("goal") || 1;
  const windowMonths = watch("windowMonths") || DEFAULT_WINDOW_MONTHS;
  const active = watch("active");
  const nameEs = watch("nameEs");
  const imageUrl = watch("imageUrl");
  const editingId =
    selection.mode === "existing" ? selection.prize.id : undefined;

  const submit = (values: GrandPrizeFormValues) => {
    const parsed = grandPrizeFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof GrandPrizeFormValues, {
          message: issue.message,
        });
      }
      return;
    }
    onSubmit(editingId, buildInput(parsed.data));
  };

  return (
    // Revisión en vivo 2026-09-22, punto #12A: el `fieldset` de botones solo
    // llevaba margen arriba y el `InfoNotice` que le sigue solo abajo — 0 + 0 =
    // 0px, se veían pegados. El espacio entre los bloques de este formulario lo
    // pone ahora el `gap` del contenedor, no un margen suelto por elemento (que
    // es justo lo que hace posible esta clase de colisión).
    <form
      onSubmit={handleSubmit(submit)}
      noValidate
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
    >
      <fieldset
        disabled={!canWrite}
        style={{ border: "none", margin: 0, padding: 0 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div>
            <h3
              className="display"
              style={{
                fontSize: 23,
                margin: "0 0 5px",
                fontWeight: 400,
                lineHeight: 1.06,
              }}
            >
              {formTitle(selection)}
            </h3>
            <div
              style={{
                font: `400 12px/1.55 ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                maxWidth: 470,
              }}
            >
              Solo puede haber un gran premio activo a la vez. Es el objetivo
              principal del programa y encabeza la app de cada miembro.
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flex: "none",
            }}
          >
            <LanguageSwitch value={language} onChange={setLanguage} />
            <span className={`stat stat-${active ? "gold" : "neutral"}`}>
              {active ? "Activo" : "Inactivo"}
            </span>
          </div>
        </div>

        {/* Sin `gap` en este contenedor: cada `GPField`/`GPFieldGroup` ya
          aplica su propio `marginBottom: 16` (idéntico a `Field` del
          prototipo, admin.jsx:83-90) como única fuente de espaciado vertical
          entre campos — un `gap` acá lo duplicaría (verificado con
          getBoundingClientRect: con `gap: 16` el campo "Imagen del premio"
          quedaba ~40px más abajo que el canónico). */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          {/* Etiquetas sin sufijo "(ES)"/"(EN)" (corrección humana
            starcircle-58, 2026-09-07): el idioma lo indica el conmutador de
            arriba, no la etiqueta del campo (ver captura canónica). */}
          <GPField
            label="Nombre del premio"
            required={language === "es"}
            error={language === "es" ? errors.nameEs?.message : undefined}
          >
            <input
              key={language}
              {...register(language === "es" ? "nameEs" : "nameEn")}
              placeholder={
                language === "en"
                  ? "Sin traducir · se mostrará en español"
                  : undefined
              }
              style={inputStyle}
            />
          </GPField>
          <GPField
            label="Descripción"
            hint="Se muestra bajo la imagen del premio en la pantalla de inicio."
          >
            <textarea
              key={language}
              {...register(
                language === "es" ? "descriptionEs" : "descriptionEn",
              )}
              placeholder={
                language === "en"
                  ? "Sin traducir · se mostrará en español"
                  : undefined
              }
              style={{ ...inputStyle, minHeight: 84, resize: "vertical" }}
            />
          </GPField>

          <GPFieldGroup
            label="Imagen del premio"
            hint="Es el hero de la app. Recomendado 1600×1000 px, JPG o PNG."
          >
            <div
              style={{
                border: `1px dashed ${tokens.surface.line2}`,
                borderRadius: 16,
                padding: 14,
              }}
            >
              {/* SPEC-208 §3.2: "muestra el encuadre resultante, con
                previsualización... antes de publicar" — el marco de
                teléfono completo es la vista previa de abajo ("Así se ve
                el avance en la app"); acá se reserva el mismo alto que el
                prototipo (250px) para el encuadre de imagen ancha. */}
              <ImageWithPlaceholder
                src={imageUrl}
                imageStyle={{
                  width: "100%",
                  height: 250,
                  objectFit: "cover",
                  borderRadius: 12,
                  border: `1px solid ${tokens.surface.line}`,
                  display: "block",
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginTop: 13,
                }}
              >
                <FileUploadChip
                  entityType="grand_prize"
                  label="Reemplazar imagen"
                  ariaLabel="Subir imagen del premio"
                  onUploaded={(image) => {
                    // PLAN-007 Fase 4d: `imageUrl` solo para la
                    // previsualización inmediata (vigente 15 minutos); el
                    // `fileId` es lo que de verdad viaja al servidor.
                    setValue("imageUrl", image.imageUrl, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setValue("imageFileId", image.fileId, {
                      shouldDirty: true,
                    });
                  }}
                />
                <span
                  style={{
                    font: `400 11px/1.5 ${tokens.font.ui}`,
                    color: tokens.text.disabled,
                  }}
                >
                  Se recorta a lo ancho en la app; deje el vehículo centrado.
                </span>
              </div>
            </div>
          </GPFieldGroup>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 18px",
              marginTop: 16,
            }}
          >
            <GPField
              label="Meta · autos comprados por referidos"
              required
              error={errors.goal?.message}
              hint="Suma de unidades, no cantidad de referidos: un miembro que compra dos autos aporta dos unidades a la meta."
            >
              <input
                type="number"
                min={1}
                {...register("goal", { valueAsNumber: true })}
                style={inputStyle}
              />
            </GPField>
            <GPField
              label="Duración de la ventana · meses"
              required
              error={errors.windowMonths?.message}
              hint="Cada compra cuenta durante este período desde su propia fecha, y luego sale sola del conteo cuando termina la ventana."
            >
              <input
                type="number"
                min={1}
                {...register("windowMonths", { valueAsNumber: true })}
                style={inputStyle}
              />
            </GPField>
          </div>

          <InfoNotice mark="i" style={{ marginBottom: 16 }}>
            <b style={{ color: tokens.text.secondary, fontWeight: 600 }}>
              No hay vigencia de ciclo, renovación automática ni arrastre.
            </b>{" "}
            El avance se deprecia de forma gradual: una compra entra al conteo
            cuando se registra y sale sola cuando cumple la duración de la
            ventana, sin reinicios de golpe.
          </InfoNotice>

          {/* No es un <GPField>: envolver botones (no un único control de
            formulario) dentro del <label> de Field duplica y rompe el
            nombre accesible de cada botón (verificado con
            @testing-library/dom — ambos chips terminaban anunciándose como
            "Estado Estado"). Mismo texto/tipografía de rótulo (.label), sin
            el wrapper <label>. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <span className="label">Estado</span>
            <div
              style={{ display: "flex", gap: 10 }}
              role="group"
              aria-label="Estado"
            >
              {(["Activo", "Inactivo"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() =>
                    setValue("active", s === "Activo", { shouldDirty: true })
                  }
                  className={
                    "chip" +
                    ((active ? "Activo" : "Inactivo") === s ? " on" : "")
                  }
                  style={{ padding: "11px 20px", fontSize: 13 }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 8,
          }}
        >
          <Can module="prizes" action={editingId ? "canEdit" : "canCreate"}>
            <PortalButton type="submit" disabled={submitting}>
              {submitting ? "Guardando…" : "Guardar y publicar"}
            </PortalButton>
          </Can>
          <button
            type="button"
            className="btn-link"
            onClick={() => {
              reset(defaultsFor(selection));
              onDiscard();
            }}
          >
            Descartar cambios
          </button>
        </div>
      </fieldset>

      <InfoNotice mark="!">
        <b style={{ color: tokens.text.secondary, fontWeight: 600 }}>
          Cambiar la meta o la ventana afecta a todos los miembros de inmediato.
        </b>{" "}
        Por eso el guardado exige confirmar el impacto antes de aplicarse.
      </InfoNotice>

      <div
        style={{
          border: `1px solid ${tokens.surface.line}`,
          borderRadius: 18,
          background: tokens.surface.bg2,
          // Mismo `Card` del prototipo (pad=22 por defecto), fuera del
          // pliegue de 900px pero con el mismo defecto de padding.
          padding: 22,
        }}
      >
        <div
          className="label"
          style={{
            marginBottom: 16,
            font: `600 12px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Así se ve el avance en la app
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 26,
            flexWrap: "wrap",
          }}
        >
          <Ring n={PREVIEW_PROGRESS} goal={goal} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div
              style={{
                font: `600 14px ${tokens.font.ui}`,
                color: tokens.text.primary,
              }}
            >
              {nameEs || "Gran premio"}
            </div>
            <div
              style={{
                font: `400 12px/1.55 ${tokens.font.ui}`,
                color: tokens.text.tertiary,
                margin: "6px 0 14px",
              }}
            >
              Faltan {Math.max(0, goal - PREVIEW_PROGRESS)} autos para alcanzar
              la meta · cada compra cuenta {windowMonths} meses desde su fecha.
            </div>
            <div className="prog prog-gold" style={{ height: 7 }}>
              <i
                style={{
                  width: `${Math.round((PREVIEW_PROGRESS / goal) * 100)}%`,
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 8,
                font: `500 11px ${tokens.font.ui}`,
                color: tokens.text.disabled,
              }}
            >
              <span>
                {PREVIEW_PROGRESS} de {goal} autos
              </span>
              <span>{Math.round((PREVIEW_PROGRESS / goal) * 100)}%</span>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

function shortDate(value: string | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function impactOf(error: ApiError): GrandPrizeImpact {
  return (error.problem?.impact as GrandPrizeImpact | undefined) ?? {};
}

type PendingConfirm = {
  code: "CONFIRM_IMPACT" | "CONFIRM_REPLACEMENT";
  id: string | undefined;
  input: GrandPrizeSaveInput;
  impact: GrandPrizeImpact;
};

export function GrandPrizeConfigScreen() {
  const toast = useToast();
  const { hasPermission } = useSession();
  const prizes = useGrandPrizes();
  const mutations = useGrandPrizeMutations();
  const [manualSelection, setManualSelection] = useState<Selection | null>(
    null,
  );
  // Cada clic en «Nuevo premio»/«Copiar el vigente» rearma el formulario
  // (aunque ya estuviera abierto) y lo trae a la vista.
  const [formRevision, setFormRevision] = useState(0);
  const formCard = useRef<HTMLDivElement>(null);
  const openNew = (seed?: GrandPrize) => {
    setManualSelection(seed ? { mode: "new", seed } : { mode: "new" });
    setFormRevision((value) => value + 1);
  };
  useEffect(() => {
    if (formRevision === 0) return;
    formCard.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    formCard.current
      ?.querySelector<HTMLElement>("input:not([type=hidden]), textarea")
      ?.focus({ preventScroll: true });
  }, [formRevision]);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
    null,
  );

  const canCreate = hasPermission("prizes", "canCreate");
  const canEdit = hasPermission("prizes", "canEdit");
  const canWrite = canCreate || canEdit;

  const list = prizes.data ?? [];
  const active = list.find((prize) => prize.active);
  const historic = list.filter((prize) => !prize.active);

  const selection: Selection =
    manualSelection ??
    (active ? { mode: "existing", prize: active } : { mode: "new" });

  const submitting = mutations.create.isPending || mutations.edit.isPending;

  // T2 (qa-bug-matrix-2026-09.md §5.5, #13): `handleError` solo reaccionaba
  // a CONFIRM_IMPACT/CONFIRM_REPLACEMENT — cualquier otro rechazo no hacía
  // nada, ni siquiera un `return` explicativo. El camino de confirmación con
  // impacto es correcto y no se toca; todo lo demás pasa por el resolutor.
  const onSubmit = (id: string | undefined, input: GrandPrizeSaveInput) => {
    const handleError = (error: unknown) => {
      if (
        error instanceof ApiError &&
        (error.code === "CONFIRM_IMPACT" ||
          error.code === "CONFIRM_REPLACEMENT")
      ) {
        setPendingConfirm({
          code: error.code,
          id,
          input,
          impact: impactOf(error),
        });
        markErrorHandled(error);
        return;
      }
      reportError(toast, error);
    };
    if (id) {
      mutations.edit.mutate(
        { id, input },
        {
          onSuccess: () => {
            setManualSelection(null);
            toast.show("Gran premio guardado correctamente.");
          },
          onError: handleError,
        },
      );
      return;
    }
    mutations.create.mutate(input, {
      onSuccess: (created) => {
        setManualSelection({ mode: "existing", prize: created });
        toast.show("Gran premio creado correctamente.");
      },
      onError: handleError,
    });
  };

  const confirmPending = () => {
    if (!pendingConfirm) return;
    const input: GrandPrizeSaveInput = {
      ...pendingConfirm.input,
      confirmed: true,
    };
    if (pendingConfirm.id) {
      mutations.edit.mutate(
        { id: pendingConfirm.id, input },
        {
          onSuccess: () => {
            setPendingConfirm(null);
            setManualSelection(null);
            toast.show("Gran premio guardado correctamente.");
          },
        },
      );
      return;
    }
    mutations.create.mutate(input, {
      onSuccess: (created) => {
        setPendingConfirm(null);
        setManualSelection({ mode: "existing", prize: created });
        toast.show("Gran premio creado correctamente.");
      },
    });
  };

  const columns: CursorTableColumn<GrandPrize>[] = [
    {
      key: "name",
      header: "PREMIO",
      render: (prize) => (
        <span
          style={{
            color: tokens.text.primary,
            font: `600 13px ${tokens.font.ui}`,
          }}
        >
          {text(prize.name, "es")}
        </span>
      ),
    },
    {
      key: "goal",
      header: "META",
      width: "120px",
      render: (prize) => <span>{prize.goal}</span>,
    },
    {
      key: "window",
      header: "VENTANA",
      width: "140px",
      render: (prize) => <span>{prize.windowMonths} meses</span>,
    },
    {
      key: "status",
      header: "ESTADO",
      width: "140px",
      render: (prize) => (
        <Badge tone={prize.active ? "achievement" : "neutral"}>
          {prize.active ? "Activo" : "Inactivo"}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      header: "CREADO",
      width: "150px",
      render: (prize) => (
        <span style={{ color: tokens.text.tertiary }}>
          {shortDate(prize.createdAt)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PrizesHeader />
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div
          ref={formCard}
          style={{
            border: `1px solid ${tokens.surface.line}`,
            borderRadius: 18,
            background: tokens.surface.bg2,
            // Card del prototipo (admin.jsx:6-7, componente `Card`) usa
            // `pad=22` por defecto — no 24 — para el marco que envuelve
            // GranPremioConfig.
            padding: 22,
          }}
        >
          {prizes.isLoading ? (
            <div style={{ padding: 24 }}>Cargando…</div>
          ) : prizes.isError ? (
            <EmptyState
              kind="error"
              action={
                <button
                  type="button"
                  className="btn"
                  onClick={() => prizes.refetch()}
                >
                  Recargar
                </button>
              }
            />
          ) : (
            <GrandPrizeForm
              key={
                selection.mode === "existing"
                  ? selection.prize.id
                  : `new-${selection.seed?.id ?? "blank"}-${formRevision}`
              }
              selection={selection}
              canWrite={canWrite}
              submitting={submitting}
              onSubmit={onSubmit}
              onDiscard={() => setManualSelection(null)}
            />
          )}
        </div>

        <div style={{ marginTop: 24 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
            }}
          >
            <div
              className="label"
              style={{
                font: `600 13px ${tokens.font.ui}`,
                color: tokens.text.secondary,
              }}
            >
              Premios configurados
            </div>
            <Can module="prizes" action="canCreate">
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  className="chip"
                  onClick={() => openNew()}
                >
                  Nuevo premio
                </button>
                {active ? (
                  <button
                    type="button"
                    className="chip"
                    onClick={() => openNew(active)}
                  >
                    Copiar el vigente
                  </button>
                ) : null}
              </div>
            </Can>
          </div>
          {list.length === 0 ? (
            <EmptyState kind="no-data" />
          ) : (
            <CursorTable
              columns={columns}
              rows={[...(active ? [active] : []), ...historic]}
              rowKey={(prize) => prize.id}
              page={{ totalCount: list.length, nextCursor: null }}
              onLoadMore={() => undefined}
              // Revisión en vivo 2026-09-22, puntos #19/#22: el punto #10
              // (barrido) había dejado esta acción en texto por ser la única
              // de la tabla; #19/#22 unifican el patrón ícono + tooltip en
              // TODA la app, tabla o card, así que converge con el resto.
              rowActions={(prize) => (
                <Can module="prizes" action="canEdit">
                  <RowIconAction
                    label="Editar"
                    icon="edit"
                    onClick={() =>
                      setManualSelection({ mode: "existing", prize })
                    }
                  />
                </Can>
              )}
              actionWidth="110px"
              minWidth={900}
              showFooter={false}
            />
          )}
        </div>
      </div>

      <ImpactConfirmDialog
        open={pendingConfirm?.code === "CONFIRM_IMPACT"}
        eyebrow="Gran premio · Configuración"
        title="Confirmar cambio de meta o ventana"
        affectedCount={pendingConfirm?.impact.affectedMembers ?? 0}
        affectedLabel={`con conteo en curso: ${pendingConfirm?.impact.wouldReachGoal ?? 0} alcanzarían la meta y ${pendingConfirm?.impact.wouldLoseProgress ?? 0} perderían avance, en promedio ${pendingConfirm?.impact.averageProgressLost ?? 0}.`}
        onCancel={() => setPendingConfirm(null)}
        onConfirm={confirmPending}
        confirmLabel="Confirmar cambio"
        busy={submitting}
      />
      <ImpactConfirmDialog
        open={pendingConfirm?.code === "CONFIRM_REPLACEMENT"}
        eyebrow="Gran premio · Configuración"
        title="Confirmar reemplazo del premio activo"
        affectedCount={pendingConfirm?.impact.affectedMembers ?? 0}
        affectedLabel="miembros conservarán su avance; el premio anterior quedará inactivo."
        onCancel={() => setPendingConfirm(null)}
        onConfirm={confirmPending}
        confirmLabel="Sí, activar"
        danger={false}
        busy={submitting}
      />
    </div>
  );
}
